/**
 * consent — LE module de consentement aux traceurs. Source unique.
 *
 * Règle absolue de ce fichier, et raison de son existence :
 *
 *   AUCUN script tiers de mesure ou de publicité n'est chargé, initialisé
 *   ou exécuté tant que le consentement n'est pas explicitement
 *   « granted ». Pas de chargement « au cas où », pas de mode dégradé,
 *   pas de pré-chargement du script en attendant la réponse.
 *
 * L'absence de choix vaut REFUS. C'est le défaut légal (art. 129 de la loi
 * du 13 juin 2005, RGPD) et c'est le défaut de ce module : un visiteur qui
 * ferme le bandeau, bloque le stockage local ou arrive avec un navigateur
 * qui refuse les cookies est traité comme un refus, jamais comme un
 * accord tacite.
 *
 * Ce que le module N'EST PAS : il ne charge rien tout seul. Les scripts
 * s'enregistrent auprès de lui (`registerThirdPartyScript`) et il les
 * exécute — une seule fois — si et quand le consentement est accordé. Un
 * script enregistré alors que le consentement est déjà accordé part
 * immédiatement ; enregistré sans consentement, il attend, et si le
 * visiteur refuse, il ne part jamais.
 *
 * Retrait du consentement : les scripts déjà exécutés dans l'onglet
 * courant ne peuvent pas être « désexécutés » (un pixel parti est parti).
 * Le module cesse en revanche immédiatement d'en lancer d'autres, et aux
 * visites suivantes plus rien ne se charge. C'est le comportement attendu
 * et c'est ce que dit la politique cookies.
 *
 * Stockage : localStorage (source primaire) + cookie de 6 mois (miroir,
 * pour que le choix survive aux navigateurs qui purgent le localStorage
 * et pour qu'un futur rendu serveur puisse le lire). Les deux portent la
 * même valeur et la DATE du choix.
 */

export const CONSENT_STORAGE_KEY = 'archer_cookie_consent';
export const CONSENT_COOKIE_NAME = 'archer_cookie_consent';

/** Durée de conservation du choix — 6 mois, comme annoncé dans /cookies. */
export const CONSENT_MAX_AGE_DAYS = 180;

export const CONSENT_GRANTED = 'granted';
export const CONSENT_DENIED = 'denied';
/** Aucun choix enregistré — se comporte comme un refus. */
export const CONSENT_UNKNOWN = 'unknown';

/** Événement émis à chaque changement de choix (le bandeau et l'app l'écoutent). */
export const CONSENT_EVENT = 'archer:consent-changed';

const isBrowser = () => typeof window !== 'undefined' && typeof document !== 'undefined';

// ── Persistance ──────────────────────────────────────────────────────

function readCookie(name) {
  if (!isBrowser()) return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, days) {
  if (!isBrowser()) return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  // `secure` seulement en HTTPS : en développement local (http://localhost)
  // un cookie `Secure` n'est tout simplement pas posé, et le choix ne
  // survivrait pas au rechargement.
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${days * 24 * 60 * 60}; Expires=${expires}; SameSite=Lax${secure}`;
}

function clearCookie(name) {
  if (!isBrowser()) return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

/**
 * Le choix stocké, ou null. Forme : { status, date } où `date` est un
 * horodatage ISO — la politique cookies promet un choix daté, conservé
 * six mois, et c'est cette date qui fait foi pour la péremption.
 */
function readRecord() {
  if (!isBrowser()) return null;
  const parse = (raw) => {
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      if (parsed && (parsed.status === CONSENT_GRANTED || parsed.status === CONSENT_DENIED)) {
        return { status: parsed.status, date: parsed.date || null };
      }
    } catch (_) { /* valeur illisible → traitée comme absente */ }
    return null;
  };

  let record = null;
  try { record = parse(window.localStorage.getItem(CONSENT_STORAGE_KEY)); } catch (_) { /* stockage bloqué */ }
  if (!record) record = parse(readCookie(CONSENT_COOKIE_NAME));
  if (!record) return null;

  // Péremption : passé six mois, on redemande. Un choix daté d'il y a un
  // an n'est plus un consentement, c'est un souvenir.
  if (record.date) {
    const age = Date.now() - new Date(record.date).getTime();
    if (Number.isFinite(age) && age > CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000) return null;
  }
  return record;
}

function writeRecord(record) {
  if (!isBrowser()) return;
  const raw = JSON.stringify(record);
  try { window.localStorage.setItem(CONSENT_STORAGE_KEY, raw); } catch (_) { /* stockage bloqué */ }
  writeCookie(CONSENT_COOKIE_NAME, raw, CONSENT_MAX_AGE_DAYS);
}

// ── État ─────────────────────────────────────────────────────────────

/** 'granted' | 'denied' | 'unknown'. `unknown` = aucun choix = refus. */
export function getConsentStatus() {
  const record = readRecord();
  return record ? record.status : CONSENT_UNKNOWN;
}

/** Date ISO du choix, ou null s'il n'y en a pas. */
export function getConsentDate() {
  const record = readRecord();
  return record ? record.date : null;
}

/** Vrai UNIQUEMENT si le visiteur a explicitement accepté. */
export const hasConsent = () => getConsentStatus() === CONSENT_GRANTED;

/** Vrai tant qu'aucun choix n'a été enregistré — c'est ce qui ouvre le bandeau. */
export const needsConsentDecision = () => getConsentStatus() === CONSENT_UNKNOWN;

function emit(status) {
  if (!isBrowser()) return;
  try {
    window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: { status } }));
  } catch (_) { /* CustomEvent indisponible — sans conséquence */ }
}

/** Le visiteur accepte : on mémorise, puis on libère les scripts en attente. */
export function acceptConsent() {
  writeRecord({ status: CONSENT_GRANTED, date: new Date().toISOString() });
  emit(CONSENT_GRANTED);
  flushPending();
}

/** Le visiteur refuse : on mémorise, et la file d'attente est jetée. */
export function denyConsent() {
  writeRecord({ status: CONSENT_DENIED, date: new Date().toISOString() });
  emit(CONSENT_DENIED);
  pending.length = 0;
}

/**
 * Efface le choix — c'est ce que fait « Gérer mes cookies » : le bandeau
 * revient et le visiteur décide à nouveau. Tant qu'il n'a pas répondu,
 * l'état est `unknown`, donc refus.
 */
export function resetConsent() {
  if (isBrowser()) {
    try { window.localStorage.removeItem(CONSENT_STORAGE_KEY); } catch (_) { /* stockage bloqué */ }
    clearCookie(CONSENT_COOKIE_NAME);
  }
  pending.length = 0;
  emit(CONSENT_UNKNOWN);
}

// ── Enregistrement des scripts tiers ─────────────────────────────────

/** Scripts enregistrés en attente d'un consentement. */
const pending = [];
/** Identifiants déjà exécutés — un pixel ne s'initialise pas deux fois. */
const executed = new Set();

function run(entry) {
  if (executed.has(entry.id)) return;
  executed.add(entry.id);
  try {
    entry.init();
  } catch (_) {
    // Un tiers qui échoue (bloqueur de pub, réseau, CSP) ne doit jamais
    // casser le site. Même règle que utils/analytics.js.
  }
}

function flushPending() {
  if (!hasConsent()) return;
  while (pending.length) run(pending.shift());
}

/**
 * Enregistre un script tiers soumis au consentement.
 *
 * @param {string}   id    identifiant stable ('tiktok-pixel', 'meta-pixel', …)
 * @param {Function} init  ce qui charge/initialise le script. N'est appelé
 *                         QUE si le consentement est accordé, et une seule fois.
 *
 * Appelable à tout moment, avant ou après le choix du visiteur.
 */
export function registerThirdPartyScript(id, init) {
  if (typeof init !== 'function') return;
  const entry = { id, init };
  if (hasConsent()) { run(entry); return; }
  if (getConsentStatus() === CONSENT_DENIED) return; // refus : on n'enfile même pas
  if (!pending.some((p) => p.id === id)) pending.push(entry);
}

/**
 * Charge une balise <script> externe. Utilitaire pour les emplacements
 * ci-dessous ; ne fait rien de particulier au consentement — c'est
 * `registerThirdPartyScript` qui décide s'il l'appelle.
 */
export function loadScript(src, attrs = {}) {
  if (!isBrowser()) return;
  const el = document.createElement('script');
  el.async = true;
  el.src = src;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
}

// ── Emplacements des pixels publicitaires ────────────────────────────
//
// Les deux blocs ci-dessous sont CÂBLÉS mais INERTES : ils lisent leur
// identifiant dans une variable d'environnement qui n'existe pas encore.
// Sans identifiant, `initTrackers()` ne les enregistre même pas. Le jour
// où Romain pose ses IDs dans l'environnement de build, les pixels
// partent — après consentement, et seulement après.
//
//   REACT_APP_TIKTOK_PIXEL_ID
//   REACT_APP_META_PIXEL_ID
//
// Aucun identifiant n'est écrit en dur ici, et aucun ne doit l'être : un
// ID en dur, c'est un pixel qui part le jour où quelqu'un supprime la
// garde par mégarde.

const TIKTOK_PIXEL_ID = process.env.REACT_APP_TIKTOK_PIXEL_ID || '';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';

/** Pixel TikTok — snippet officiel, réduit à ce qui l'initialise. */
function initTikTokPixel() {
  /* eslint-disable */
  (function (w, d, t) {
    w.TiktokAnalyticsObject = t;
    var ttq = (w[t] = w[t] || []);
    ttq.methods = ['page', 'track', 'identify', 'instances', 'debug', 'on', 'off', 'once', 'ready', 'alias', 'group', 'enableCookie', 'disableCookie'];
    ttq.setAndDefer = function (obj, method) {
      obj[method] = function () { obj.push([method].concat(Array.prototype.slice.call(arguments, 0))); };
    };
    for (var i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
    ttq.instance = function (id) {
      var inst = ttq._i[id] || [];
      for (var j = 0; j < ttq.methods.length; j++) ttq.setAndDefer(inst, ttq.methods[j]);
      return inst;
    };
    ttq.load = function (id, options) {
      var url = 'https://analytics.tiktok.com/i18n/pixel/events.js';
      ttq._i = ttq._i || {}; ttq._i[id] = []; ttq._i[id]._u = url;
      ttq._t = ttq._t || {}; ttq._t[id] = +new Date();
      ttq._o = ttq._o || {}; ttq._o[id] = options || {};
      var script = d.createElement('script');
      script.type = 'text/javascript'; script.async = true; script.src = url + '?sdkid=' + id + '&lib=' + t;
      var first = d.getElementsByTagName('script')[0];
      first.parentNode.insertBefore(script, first);
    };
    ttq.load(TIKTOK_PIXEL_ID);
    ttq.page();
  })(window, document, 'ttq');
  /* eslint-enable */
}

/** Pixel Meta (Facebook) — snippet officiel, réduit à ce qui l'initialise. */
function initMetaPixel() {
  /* eslint-disable */
  (function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
    if (!f._fbq) f._fbq = n;
    n.push = n; n.loaded = true; n.version = '2.0'; n.queue = [];
    t = b.createElement(e); t.async = true; t.src = v;
    s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  window.fbq('init', META_PIXEL_ID);
  window.fbq('track', 'PageView');
  /* eslint-enable */
}

/**
 * Google Analytics — présent dans le repo AVANT ce chantier
 * (components/seo/GoogleAnalytics.jsx chargeait gtag.js sans aucune
 * garde, dès que REACT_APP_GA_MEASUREMENT_ID était défini). gtag pose des
 * cookies : il passe désormais par ici comme les deux pixels.
 */
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || '';

function initGoogleAnalytics() {
  loadScript(`https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`);
  /* eslint-disable */
  window.dataLayer = window.dataLayer || [];
  function gtag() { window.dataLayer.push(arguments); }
  window.gtag = window.gtag || gtag;
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, { anonymize_ip: true });
  /* eslint-enable */
}

/**
 * Déclare les traceurs soumis au consentement. Appelé une fois au
 * démarrage de l'application : rien ne part tant que le visiteur n'a pas
 * accepté, et rien ne part du tout si aucun identifiant n'est configuré.
 */
export function initTrackers() {
  if (!isBrowser()) return;
  if (TIKTOK_PIXEL_ID) registerThirdPartyScript('tiktok-pixel', initTikTokPixel);
  if (META_PIXEL_ID) registerThirdPartyScript('meta-pixel', initMetaPixel);
  if (GA_MEASUREMENT_ID) registerThirdPartyScript('google-analytics', initGoogleAnalytics);
}

export default {
  getConsentStatus,
  getConsentDate,
  hasConsent,
  needsConsentDecision,
  acceptConsent,
  denyConsent,
  resetConsent,
  registerThirdPartyScript,
  initTrackers,
};
